// ========= START: src/components/CharacterCustomizationScreen.js (COMPLETE REPLACEMENT) =========
import React, { useState, useEffect } from 'react';
import { CUSTOMIZATION_ITEMS } from '../data/character_customization_config';
import Modal from './Modal';

const characterBaseImage = `${process.env.PUBLIC_URL}/assets/images/character_base.png`;
const TAB_ORDER = ['eyes', 'hair', 'glasses', 'hats', 'clothes'];
const RENDER_ORDER = ['clothes', 'eyes', 'hair', 'glasses', 'hats'];

function CharacterCustomizationScreen({ user, onBack, onSavePlayerData }) {
  const [username, setUsername] = useState(user.username || '');
  const [equippedItems, setEquippedItems] = useState(user.equipped || {});
  const [activeCategory, setActiveCategory] = useState(TAB_ORDER[0]);

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    previewImage: null,
    onConfirm: () => { },
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });

  useEffect(() => {
    setUsername(user.username || '');
    setEquippedItems(user.equipped || {});
  }, [user]);

  const handleCloseModal = () => {
    setModalConfig({ ...modalConfig, isOpen: false });
  };

  // ========= START: CharacterCustomizationScreen.js - handleAccessoryPress function replacement =========
  const handleAccessoryPress = (item, category) => {
    const isUnlocked = user.unlockedItems && user.unlockedItems[item.id];

    if (isUnlocked) {
      setEquippedItems(prev => {
        const newEquipped = { ...prev };
        Object.values(CUSTOMIZATION_ITEMS[category]).forEach(catItem => {
          delete newEquipped[catItem.id];
        });
        if (!prev[item.id]) {
          newEquipped[item.id] = true;
        }
        return newEquipped;
      });
    } else if (user.unlockPoints >= item.price) {
      // User can afford the item, show confirmation modal
      setModalConfig({
        isOpen: true,
        title: 'Confirm Unlock',
        message: `Spend ${item.price} unlock point${item.price > 1 ? 's' : ''} to get the "${item.name}"?`,
        previewImage: item.icon,
        onConfirm: () => {
          // FIX: Include current username and equippedItems so unsaved changes aren't lost
          const updatedPlayerData = {
            ...user,
            username: username,
            equipped: equippedItems
          };

          if (!updatedPlayerData.unlockedItems) {
            updatedPlayerData.unlockedItems = {};
          }
          updatedPlayerData.unlockPoints -= item.price;
          updatedPlayerData.unlockedItems[item.id] = true;
          onSavePlayerData(updatedPlayerData);
        },
        confirmText: 'Confirm',
        cancelText: 'Cancel'
      });
    } else {
      // User cannot afford the item, show "not enough points" modal
      setModalConfig({
        isOpen: true,
        title: 'Not Enough Points',
        message: `You need ${item.price} point${item.price > 1 ? 's' : ''} to unlock this item.`,
        previewImage: item.icon,
        onConfirm: handleCloseModal,
        confirmText: 'OK',
        cancelText: null
      });
    }
  };
  // ========= END: CharacterCustomizationScreen.js - handleAccessoryPress function replacement =========

  const handleSaveChanges = () => {
    const updatedPlayerData = { ...user };
    let hasChanges = false;
    if (username.trim() && username.trim() !== user.username) {
      updatedPlayerData.username = username.trim();
      hasChanges = true;
    }
    if (JSON.stringify(equippedItems) !== JSON.stringify(user.equipped || {})) {
      updatedPlayerData.equipped = equippedItems;
      hasChanges = true;
    }
    if (hasChanges) {
      onSavePlayerData(updatedPlayerData);
    }
  };

  const renderItemGrid = () => {
    const items = CUSTOMIZATION_ITEMS[activeCategory] || [];
    return (
      <div className="accessory-grid">
        {items.map(item => {
          const isUnlocked = user.unlockedItems && user.unlockedItems[item.id];
          const isEquipped = equippedItems[item.id];
          const canUnlock = user.unlockPoints >= item.price;
          let buttonClass = 'accessory-button';
          if (isEquipped) buttonClass += ' equipped';
          if (!isUnlocked) buttonClass += ' locked';

          return (
            <button
              key={item.id}
              className={buttonClass}
              onClick={() => handleAccessoryPress(item, activeCategory)}
            >
              <img src={item.icon} alt={item.name} className="accessory-icon" />
              {!isUnlocked && (
                <>
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/menu-buttons/${item.price}_point${item.price > 1 ? 's' : ''}.png`}
                    alt={`${item.price} points`}
                    className="item-price-tag"
                  />
                  <div className="lock-overlay">
                    <span className="lock-text">
                      {canUnlock ? 'Unlock!' : 'Locked'}
                    </span>
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="character-screen">
        <div className="character-screen-header">
          <h2 className="character-screen-title">Customize Character</h2>
          <div className="unlock-points-display">
            Unlock Points: <span>{user.unlockPoints || 0}</span>
          </div>
        </div>

        <div className="character-name-editor">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="character-name-input"
            maxLength="12"
          />
        </div>

        <div className="character-main-content">
          <div className="character-preview-container">
            <img src={characterBaseImage} alt="Character Base" className="character-base-img" />
            {RENDER_ORDER.map(categoryKey =>
              CUSTOMIZATION_ITEMS[categoryKey].map(item => {
                if (equippedItems[item.id]) {
                  return <img key={item.id} src={item.image} alt={item.name} className={`accessory-img item-${item.id}`} />;
                }
                return null;
              })
            )}
          </div>

          <div className="accessory-selection-container">
            <div className="category-tabs">
              {TAB_ORDER.map(categoryKey => (
                <button
                  key={categoryKey}
                  className={`category-tab-button ${activeCategory === categoryKey ? 'active' : ''}`}
                  onClick={() => setActiveCategory(categoryKey)}
                >
                  {categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1)}
                </button>
              ))}
            </div>
            {renderItemGrid()}
          </div>
        </div>

        <button onClick={handleSaveChanges} className="stats-button">
          Save Changes
        </button>
      </div>

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={handleCloseModal}
        onConfirm={() => {
          modalConfig.onConfirm();
          handleCloseModal();
        }}
        title={modalConfig.title}
        previewImage={modalConfig.previewImage}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      >
        <p>{modalConfig.message}</p>
      </Modal>
    </>
  );
};

export default CharacterCustomizationScreen;
// ========= END: src/components/CharacterCustomizationScreen.js (COMPLETE REPLACEMENT) =========